import React, { useState } from 'react';
import { VALID_EMAIL_REGEX } from '../../../core/constants/constants';
import { TeamService } from '../../../core/services/team.service';
import TeamAPI from '../api/TeamAPI';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import {
  TRACKER_INVITE_MEMBER_DIALOG_CLICK,
  TRACKER_INVITE_MEMBER_DIALOG_ERROR,
  TRACKER_INVITE_MEMBER_DIALOG_SUCCESS
} from './constants';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import FormError from '../../../form/components/form-error/FormError';
import { HdButton, HdIcon, HdIconButton, HdModal } from '../../../components';
import { HdTextField } from '../../../input/components/text-field/HdTextField';

export function AddMemberDialog({
  open,
  ...others
}: {
  open: boolean;
} & AddMemberDialogInnerProps) {
  return (
    <HdModal
      open={open}
      onClose={() => others.onClose()}
      placement='top'
      styling={{ width: '470px' }}
    >
      <AddMemberDialogInner {...others} />
    </HdModal>
  );
}

interface AddMemberDialogInnerProps {
  dialogBodyData;
  onClose: Function;
}

function AddMemberDialogInner({ dialogBodyData, onClose }) {
  const { entity, type, loginNextURL, wizard } = dialogBodyData;
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteSendError, setInviteSendError] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState(false);

  const { eventTrack } = useAnalyticsTracker();
  const _teamService = useService(TeamService);

  const dataIdGenerator = getDataIdGenerator('add-member-dialog');

  const inviteOptions = {
    entity,
    entity_type: type,
    redirect_url: loginNextURL,
    wizard
  };

  const sendMemberInvite = async () => {
    try {
      setSendingInvite(true);
      setInviteSendError(null);

      eventTrack({
        action: TRACKER_INVITE_MEMBER_DIALOG_CLICK,
        properties: { ...inviteOptions, email }
      });

      await TeamAPI.inviteMember({ ...inviteOptions, email });
      setSendingInvite(false);
      setInviteSent(true);

      eventTrack({
        action: TRACKER_INVITE_MEMBER_DIALOG_SUCCESS,
        properties: { ...inviteOptions, email }
      });
    } catch (error) {
      setSendingInvite(false);

      const formattedError = getErrorMessageFromObj(error);
      setInviteSendError(formattedError);

      eventTrack({
        action: TRACKER_INVITE_MEMBER_DIALOG_ERROR,
        properties: {
          error: formattedError,
          email,
          ...inviteOptions
        }
      });
    }
  };

  const onEmailChangeHandler = value => {
    setFieldError(false);

    if (!value || !VALID_EMAIL_REGEX.test(value)) {
      setFieldError(true);
    }
    setEmail(value);
  };

  return (
    <>
      <div className='dialog-header dialog-header--absolute'>
        <HdIconButton dataId={dataIdGenerator('close-btn')} onClick={onClose}>
          <HdIcon name='close' />
        </HdIconButton>
      </div>

      <div className='dialog-body'>
        {!inviteSent ? (
          <>
            <div className='text-subheading-3 mt-1'>Invite a Team Member</div>

            <p className='text-secondary mt-3'>
              Invite a team member who has the required credentials to configure the{' '}
              {entity.toLowerCase()} for you.
            </p>

            <HdTextField
              label='Email Address'
              className='form-field-full-width mt-7 pb-5'
              dataId={dataIdGenerator('email-field')}
              error={fieldError}
              type='email'
              placeholder={"Team member's email address"}
              disabled={sendingInvite}
              onChange={event => onEmailChangeHandler(event.target.value)}
            />

            {inviteSendError?.length ? (
              <FormError className='mb-5'>{inviteSendError}</FormError>
            ) : null}
          </>
        ) : (
          <div className='no-item-box my-5'>
            <div className='no-item-box-icon-container'>
              <HdIcon className='no-item-box-icon' name='email' />
            </div>

            <span className='no-item-box-title'>Invitation sent 👏</span>

            <span className='no-item-box-desc'>
              We have sent an email to your team member with instructions on how to configure the{' '}
              {entity.toLowerCase()}.
            </span>
          </div>
        )}
      </div>

      {!inviteSent ? (
        <div className='dialog-footer'>
          <HdButton dataId={dataIdGenerator('cancel')} variation='outline' onClick={onClose}>
            Cancel
          </HdButton>

          <HdButton
            dataId={dataIdGenerator('send invite')}
            disabled={sendingInvite}
            showProgress={sendingInvite}
            onClick={sendMemberInvite}
          >
            {sendingInvite ? 'Sending Invite' : 'Send An Invite'}
          </HdButton>
        </div>
      ) : null}
    </>
  );
}
